import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { StarRatingComponent } from '../ui/star-rating.component';
import { ActionButtonComponent } from '../buttons/action-button.component';
import { BorderIconButtonComponent } from '../buttons/border-icon-button.component';
import { AppStateService } from '../../services/app-state.service';
import { TranslateService } from '../../services/translate.service';
import { ReviewService } from '../../services/review.service';
import { Subscription, firstValueFrom, filter, take, switchMap } from 'rxjs';
import { Auth, user } from '@angular/fire/auth';

@Component({
  selector: 'app-review',
  standalone: true,
  imports: [StarRatingComponent, ActionButtonComponent, BorderIconButtonComponent],
  styles: [`
    @keyframes fadeSlideIn {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-slide-in {
      animation: fadeSlideIn 0.35s ease-out both;
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.5); }
      to { opacity: 1; transform: scale(1); }
    }
    .animate-scale-in {
      animation: scaleIn 0.4s ease-out both;
    }
  `],
  template: `
    <div class="max-w-lg mx-auto min-h-[calc(100dvh-5rem)] bg-theme-surface-95 backdrop-blur-2xl">
      <div class="h-20"></div>
      <div class="flex flex-col items-center px-6 pt-16">
        @if (reviewExpired) {
          <div class="w-full flex flex-col items-center gap-6 animate-fade-slide-in">
            <div class="w-20 h-20 rounded-full bg-theme-primary flex items-center justify-center animate-scale-in">
              <i class="fa-solid fa-clock text-theme-contrast text-3xl"></i>
            </div>
            <div class="text-2xl font-bold text-theme-text text-center">
              {{ getString(reviewService.expiredText) }}
            </div>
            @if (showGoogleReview) {
              <div class="w-full mt-2">
                <app-action-button
                  [text]="getString('Betygsätt på Google')"
                  icon="fa-arrow-up-right-from-square"
                  [chevron]="false"
                  (onClick)="openGoogle()">
                </app-action-button>
              </div>
            }
          </div>
        } @else if (confirmationShown) {
          <div class="w-full flex flex-col items-center gap-6 animate-fade-slide-in">
            <div class="w-20 h-20 rounded-full bg-theme-primary flex items-center justify-center animate-scale-in">
              <i class="fa-solid fa-check text-theme-contrast text-3xl"></i>
            </div>
            <div class="text-2xl font-bold text-theme-text text-center">
              {{ getString(reviewService.thankYouText) }}
            </div>
            @if (showGoogleReview) {
              <div class="w-full mt-2">
                <app-action-button
                  [text]="getString('Betygsätt på Google')"
                  icon="fa-arrow-up-right-from-square"
                  [chevron]="false"
                  (onClick)="openGoogle()">
                </app-action-button>
              </div>
            }
          </div>
        } @else {
          <div class="text-2xl font-bold text-theme-text text-center">
            {{ getString(reviewService.reviewText) }}
          </div>

          <div class="mt-6 flex justify-center">
            <app-star-rating [rating]="overallRating" [disabled]="reviewLocked"
              (ratingChange)="onOverallRatingChanged($event)" size="lg">
            </app-star-rating>
          </div>

          @if (overallRating > 0 && firstSaveCompleted) {
          <div class="w-full mt-10 flex flex-col gap-6 animate-fade-slide-in">
            @if (reviewService.showFood) {
              <div class="flex items-center justify-between">
                <span class="text-lg font-medium text-theme-text">{{ getString('Mat & Dryck') }}</span>
                <app-star-rating [rating]="ratingFood" [disabled]="reviewLocked" (ratingChange)="ratingFood = $event" size="md"></app-star-rating>
              </div>
            }
            @if (reviewService.showService) {
              <div class="flex items-center justify-between">
                <span class="text-lg font-medium text-theme-text">{{ getString('Service') }}</span>
                <app-star-rating [rating]="ratingService" [disabled]="reviewLocked" (ratingChange)="ratingService = $event" size="md"></app-star-rating>
              </div>
            }
            @if (reviewService.showAmbiance) {
              <div class="flex items-center justify-between">
                <span class="text-lg font-medium text-theme-text">{{ getString('Atmosfär') }}</span>
                <app-star-rating [rating]="ratingAmbiance" [disabled]="reviewLocked" (ratingChange)="ratingAmbiance = $event" size="md"></app-star-rating>
              </div>
            }
            @if (reviewService.showValue) {
              <div class="flex items-center justify-between">
                <span class="text-lg font-medium text-theme-text">{{ getString('Prisvärdhet') }}</span>
                <app-star-rating [rating]="ratingValue" [disabled]="reviewLocked" (ratingChange)="ratingValue = $event" size="md"></app-star-rating>
              </div>
            }

            <div class="mt-4 flex flex-col gap-3">
              @if (showGoogleReview) {
                <app-action-button
                  [text]="getString('Betygsätt på Google')"
                  icon="fa-arrow-up-right-from-square"
                  [chevron]="false"
                  [spinner]="saving"
                  [disabled]="saving"
                  (onClick)="saveAndOpenGoogle()">
                </app-action-button>
              }
              @if (!reviewLocked) {
                <app-border-icon-button
                  [text]="getString('Skicka in')"
                  [disabled]="saving"
                  (onClick)="saveAll()">
                </app-border-icon-button>
              }
            </div>
          </div>
          }
        }
      </div>
    </div>
  `
})
export class ReviewComponent implements OnInit, OnDestroy {
  overallRating = 0;
  ratingFood = 0;
  ratingService = 0;
  ratingAmbiance = 0;
  ratingValue = 0;
  showGoogleReview = false;
  confirmationShown = false;
  reviewExpired = false;
  saving = false;
  reviewLocked = false;
  firstSaveCompleted = false;
  private googleReviewDecided = false;
  private loadedRating?: number;
  private receiptKey?: string;
  private sub?: Subscription;
  private lastAutoSavedRating = 0;
  private debounceTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private appStateService: AppStateService,
    private translate: TranslateService,
    public reviewService: ReviewService,
    private auth: Auth
  ) {}

  ngOnInit(): void {
    this.receiptKey = this.route.snapshot.queryParamMap.get('receipt') ?? undefined;

    this.sub = this.appStateService.observeAppState().subscribe(appState => {
      this.reviewService.loadConfig(appState.cfg);
      if (!this.showGoogleReview && this.loadedRating !== undefined) {
        if (this.reviewService.canShowGoogleReview(this.loadedRating)) {
          this.showGoogleReview = true;
        }
      }
    });

    if (this.receiptKey) {
      this.reviewLocked = true;
      user(this.auth).pipe(
        filter(u => u != null),
        take(1),
        switchMap(() => this.reviewService.fetchReview(this.receiptKey!))
      ).subscribe({
        next: review => {
          if (review?.rating) {
            this.overallRating = review.rating;
            this.ratingFood = review.rating_food;
            this.ratingService = review.rating_service;
            this.ratingAmbiance = review.rating_ambiance;
            this.ratingValue = review.rating_value;
            this.lastAutoSavedRating = review.rating;
            this.loadedRating = review.rating;
            this.firstSaveCompleted = true;
            if (!this.googleReviewDecided && this.reviewService.canShowGoogleReview(review.rating)) {
              this.showGoogleReview = true;
            }
            this.googleReviewDecided = true;
            if (review.locked) {
              this.confirmationShown = true;
            }
            this.reviewLocked = !!review.locked;
          } else {
            this.reviewLocked = false;
          }
        },
        error: e => {
          console.warn('[Review] Failed to fetch review:', e);
          this.reviewLocked = false;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    clearTimeout(this.debounceTimer);
  }

  onOverallRatingChanged(rating: number) {
    this.overallRating = rating;
    if (!this.googleReviewDecided) {
      this.googleReviewDecided = true;
      if (this.reviewService.canShowGoogleReview(rating)) {
        this.showGoogleReview = true;
      }
    }
    this.autoSave();
  }

  getString(s: string): string {
    return this.translate.getString(s);
  }

  private autoSave() {
    if (this.overallRating === 0 || this.reviewLocked || this.reviewExpired) return;
    if (this.overallRating === this.lastAutoSavedRating) return;
    clearTimeout(this.debounceTimer);
    const delay = this.firstSaveCompleted ? 500 : 0;
    this.debounceTimer = setTimeout(async () => {
      if (!this.receiptKey) return;
      if (this.overallRating === this.lastAutoSavedRating) return;
      try {
        await firstValueFrom(this.reviewService.submitReview({
          date: new Date().toISOString(),
          type: 'qr_code',
          rating: this.overallRating,
          rating_food: this.ratingFood,
          rating_service: this.ratingService,
          rating_ambiance: this.ratingAmbiance,
          rating_value: this.ratingValue,
          receipt_key: this.receiptKey!
        }));
        this.lastAutoSavedRating = this.overallRating;
        this.firstSaveCompleted = true;
      } catch (e: any) {
        console.warn('[Review] Auto-save failed:', e);
        if (e?.status === 403 || e?.status === 410) {
          if (this.lastAutoSavedRating > 0) {
            this.reviewLocked = true;
          } else {
            this.reviewExpired = true;
          }
        }
      }
    }, delay);
  }

  private async submitReview() {
    if (!this.receiptKey || this.reviewLocked) return;
    try {
      await firstValueFrom(this.reviewService.submitReview({
        date: new Date().toISOString(),
        type: 'qr_code',
        rating: this.overallRating,
        rating_food: this.ratingFood,
        rating_service: this.ratingService,
        rating_ambiance: this.ratingAmbiance,
        rating_value: this.ratingValue,
        receipt_key: this.receiptKey!
      }));
    } catch (e: any) {
      console.warn('[Review] Save failed:', e);
      if (e?.status === 403 || e?.status === 410) {
        if (this.lastAutoSavedRating > 0) {
          this.reviewLocked = true;
        } else {
          this.reviewExpired = true;
        }
      }
    }
  }

  async saveAll() {
    if (this.saving) return;
    this.saving = true;
    try {
      await this.submitReview();
    } finally {
      this.saving = false;
    }
    if (!this.reviewExpired) {
      this.confirmationShown = true;
    }
  }

  openGoogle() {
    const url = this.reviewService.getGoogleReviewUrl();
    if (url) window.open(url, '_blank');
  }

  async saveAndOpenGoogle() {
    if (this.saving) return;
    this.saving = true;
    try {
      await this.submitReview();
    } finally {
      this.saving = false;
    }
    if (!this.reviewExpired) {
      this.confirmationShown = true;
    }
    const url = this.reviewService.getGoogleReviewUrl();
    if (url) window.open(url, '_blank');
  }
}
